/**
 * ETrax demo-mode bootstrap.
 *
 * When active, intercepts BOTH transports the app uses to reach `/api/*`:
 *   1. the axios singleton (`apiClient`) — most service calls
 *   2. `window.fetch` — Login, AuthProvider, Scan, Voice, Reports, admin pages
 * and routes them to the in-browser demo server (./server.ts). Also seeds a
 * fake authenticated session so the protected app shell renders immediately.
 *
 * Activation (any of):
 *   - localStorage `etrax_demo` === '1'  (persisted; set by the demo entry)
 *   - `?demo` query param on any URL
 *   - VITE_DEMO_MODE === 'true' at build time
 */
import { apiClient } from '../services/api';
import { handleDemoRequest } from './server';
import { DEMO_USER, DEMO_TOKEN, DEMO_REFRESH_TOKEN } from './data';

const DEMO_FLAG = 'etrax_demo';

export function isDemoMode(): boolean {
  try {
    const flagged = localStorage.getItem(DEMO_FLAG) === '1';
    const queried =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('demo');
    const envOn = import.meta.env.VITE_DEMO_MODE === 'true';
    return flagged || queried || envOn;
  } catch {
    return import.meta.env.VITE_DEMO_MODE === 'true';
  }
}

function seedDemoSession() {
  const user = JSON.stringify(DEMO_USER);
  localStorage.setItem(DEMO_FLAG, '1');
  localStorage.setItem('accessToken', DEMO_TOKEN);
  localStorage.setItem('refreshToken', DEMO_REFRESH_TOKEN);
  localStorage.setItem('authToken', DEMO_TOKEN); // AuthProvider convention
  localStorage.setItem('user', user);
}

/** Turn demo mode on and drop the visitor into the dashboard. */
export function enableDemoMode() {
  seedDemoSession();
  window.location.replace('/app/dashboard');
}

/** Turn demo mode off and clear the fabricated session. */
export function disableDemoMode() {
  [
    DEMO_FLAG,
    'accessToken',
    'refreshToken',
    'authToken',
    'user',
    'recentScans',
    'etrax_equipment_cache',
  ].forEach((k) => localStorage.removeItem(k));
  window.location.replace('/');
}

function parseBody(body: any): any {
  if (body == null) return undefined;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return Object.fromEntries(body.entries());
  }
  return body;
}

function isApiUrl(url: string): boolean {
  return /\/api(\/|$|\?)/.test(url);
}

let installed = false;

export function installDemo() {
  if (installed || (typeof window !== 'undefined' && (window as any).__etraxDemo)) return;
  installed = true;
  (window as any).__etraxDemo = true;

  // Always make sure a demo session exists so the protected shell renders.
  seedDemoSession();

  // 1) Patch the axios singleton's adapter.
  apiClient.getRawClient().defaults.adapter = async (config: any) => {
    const base = config.baseURL || '';
    let url = (base ? base.replace(/\/$/, '') : '') + (config.url || '');
    if (config.params) {
      const qs = new URLSearchParams(config.params as any).toString();
      if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }
    const { status, data } = handleDemoRequest(config.method || 'get', url, parseBody(config.data), 'axios');
    return {
      data,
      status,
      statusText: 'OK',
      headers: { 'content-type': 'application/json' },
      config,
      request: {},
    };
  };

  // 2) Patch window.fetch for raw-fetch callers.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: Request | string | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
    if (!isApiUrl(url)) return originalFetch(input as any, init);

    const method = (init?.method || (input instanceof Request ? input.method : 'GET') || 'GET').toString();
    let body: any = init?.body;
    if (body == null && input instanceof Request) {
      try {
        body = await input.clone().text();
      } catch {
        /* ignore */
      }
    }
    const { status, data, contentType } = handleDemoRequest(method, url, parseBody(body), 'fetch');
    // Raw responses (e.g. CSV export/template) are returned verbatim; everything else is JSON.
    const isRaw = typeof contentType === 'string';
    return new Response(isRaw ? (data as string) : JSON.stringify(data), {
      status,
      headers: { 'Content-Type': isRaw ? (contentType as string) : 'application/json' },
    });
  };

  // eslint-disable-next-line no-console
  console.info('%cETrax demo mode active — sample data, changes reset on reload.', 'color:#f5b301;font-weight:bold');
}
